/**
 * Cookie Management Utilities
 */

/**
 * Set a cookie with specified name, value, and expiration
 * @param name - Cookie name
 * @param value - Cookie value
 * @param days - Expiration in days (default: 365)
 * @param domain - Domain for cookie (optional)
 * @returns The cookie value that was set
 */
export function setCookie(
  name: string,
  value: string,
  days = 365,
  domain?: string
): string | undefined {
  const expires = new Date(
    Date.now() + days * 24 * 60 * 60 * 1000
  ).toUTCString();

  let cookieString = `${name}=${value}; expires=${expires}; path=/`;

  // Handle domain configuration with localhost detection
  if (domain && domain.trim() !== '') {
    if (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      /^\d+\.\d+\.\d+\.\d+$/.test(window.location.hostname)
    ) {
      // For localhost/IP addresses, don't set domain attribute
    } else {
      const domainValue = domain.startsWith('.') ? domain : `.${domain}`;
      cookieString += `; domain=${domainValue}`;
    }
  }

  // Set SameSite policy for security
  cookieString += `; SameSite=Lax`;
  document.cookie = cookieString;

  return getCookie(name);
}

/**
 * Get cookie value by name
 * @param name - Cookie name to retrieve
 * @returns Cookie value or undefined if not found
 */
export function getCookie(name: string): string | undefined {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift();
  }
  return undefined;
}

/**
 * Delete a cookie by setting expiration date in the past
 * @param name - Cookie name to delete
 * @param domain - Domain for cookie (optional)
 */
export function deleteCookie(name: string, domain?: string): void {
  let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;

  // Use same domain logic as setCookie for consistency
  if (domain && domain.trim() !== '') {
    if (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      /^\d+\.\d+\.\d+\.\d+$/.test(window.location.hostname)
    ) {
      // For localhost/IP addresses, don't set domain attribute
    } else {
      const domainValue = domain.startsWith('.') ? domain : `.${domain}`;
      cookieString += `; domain=${domainValue}`;
    }
  }

  cookieString += `; SameSite=Lax`;
  document.cookie = cookieString;
}
